import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ShieldCheck, ShieldOff, Copy, LogOut, ScrollText } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const EVENT_LABELS = {
  register: 'Inscription',
  email_verified: 'Email confirmé',
  login_success: 'Connexion réussie',
  login_failed: 'Échec de connexion',
  logout_all: 'Déconnexion de toutes les sessions',
  refresh_reuse_detected: 'Jeton rejoué — sessions révoquées',
  rate_limited: 'Tentatives bloquées (rate limit)',
  mfa_enabled: 'MFA activée',
  mfa_disabled: 'MFA désactivée',
  mfa_failed: 'Échec code MFA',
  mfa_enable_failed: 'Échec activation MFA',
  mfa_recovery_code_used: 'Code de récupération utilisé',
  password_changed: 'Mot de passe modifié',
  password_reset_requested: 'Réinitialisation demandée',
  password_reset_completed: 'Mot de passe réinitialisé',
  member_added: 'Membre ajouté',
  member_removed: 'Membre supprimé',
  company_mfa_required_enabled: 'MFA exigée pour l\'entreprise',
  company_mfa_required_disabled: 'Exigence MFA levée',
};

export default function SecuritySection() {
  const { user, company, logout, refreshMe } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // Enrôlement MFA inline
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  // Désactivation
  const [showDisable, setShowDisable] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  // Journal
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState(null);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/mfa/setup');
      setSetup(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/mfa/enable', { code: code.trim() });
      setRecoveryCodes(data.recoveryCodes);
      setSetup(null);
      setCode('');
      await refreshMe();
      toast.success('Double authentification activée');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Code incorrect');
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/mfa/disable', disableForm);
      setShowDisable(false);
      setDisableForm({ password: '', code: '' });
      if (data?.loggedOut) {
        // Super-administrateur : sessions fermées côté serveur, ré-enrôlement à la prochaine connexion.
        toast.success(data.message);
        await logout();
        return;
      }
      await refreshMe();
      toast.success('Double authentification désactivée');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const toggleOrgMfa = async () => {
    const next = !company?.mfaRequired;
    if (next && !window.confirm(
      'Exiger la double authentification pour tous les membres ? '
      + 'Chacun devra la configurer à sa prochaine connexion.'
    )) return;
    try {
      const { data } = await api.put('/users/company/mfa', { required: next });
      await refreshMe();
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const loadLog = useCallback(async () => {
    try {
      const { data } = await api.get('/users/audit-log');
      setLog(data);
    } catch {
      toast.error('Erreur de chargement du journal');
    }
  }, []);

  useEffect(() => {
    if (showLog && !log) loadLog();
  }, [showLog, log, loadLog]);

  const logoutAll = async () => {
    if (!window.confirm('Déconnecter toutes vos sessions sur tous vos appareils ?')) return;
    try {
      await api.post('/auth/logout-all');
      await logout();
      window.location.href = '/login';
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: 12 }}>Sécurité</h3>

      {/* MFA personnelle */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {user?.mfaEnabled
            ? <ShieldCheck size={18} color="#16a34a" />
            : <ShieldOff size={18} color="#94a3b8" />}
          <strong style={{ fontSize: '0.95rem' }}>
            Double authentification {user?.mfaEnabled ? 'activée' : 'désactivée'}
          </strong>
        </div>

        {!user?.mfaEnabled && !setup && !recoveryCodes && (
          <button className="btn btn-primary btn-sm" onClick={startSetup} disabled={busy}>
            Activer la double authentification
          </button>
        )}

        {setup && (
          <div style={{ marginTop: 10 }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 8 }}>
              Scannez ce QR code avec votre application d'authentification
              (Google Authenticator, Authy...) puis saisissez le code à 6 chiffres.
            </p>
            <img src={setup.qrCode} alt="QR code MFA" style={{ width: 170, height: 170, display: 'block', marginBottom: 6 }} />
            <p style={{ fontSize: '0.72rem', color: 'var(--text-light)', wordBreak: 'break-all', marginBottom: 8 }}>
              Saisie manuelle : <code>{setup.secret}</code>
            </p>
            <form onSubmit={enable} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={code} onChange={e => setCode(e.target.value)} required
                inputMode="numeric" pattern="[0-9]{6}" placeholder="123456"
                style={{ width: 120, textAlign: 'center', letterSpacing: 3 }} />
              <button className="btn btn-primary btn-sm" disabled={busy}>Confirmer</button>
              <button type="button" className="btn btn-sm" onClick={() => { setSetup(null); setCode(''); }}>
                Annuler
              </button>
            </form>
          </div>
        )}

        {recoveryCodes && (
          <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14 }}>
            <p style={{ fontSize: '0.85rem', color: '#166534', marginBottom: 8 }}>
              <strong>Conservez ces codes de récupération</strong> (usage unique).
              Ils ne seront plus jamais affichés.
            </p>
            <div style={{
              fontFamily: 'monospace', fontSize: '0.9rem',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8,
            }}>
              {recoveryCodes.map(c => <span key={c}>{c}</span>)}
            </div>
            <button className="btn btn-sm" onClick={() => {
              navigator.clipboard?.writeText(recoveryCodes.join('\n'))
                .then(() => toast.success('Codes copiés')).catch(() => {});
            }}>
              <Copy size={13} /> Copier
            </button>
            <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => setRecoveryCodes(null)}>
              J'ai noté mes codes
            </button>
          </div>
        )}

        {user?.mfaEnabled && !showDisable && (
          <button className="btn btn-sm" onClick={() => setShowDisable(true)}>Désactiver</button>
        )}
        {showDisable && (
          <form onSubmit={disable} style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="password" placeholder="Mot de passe" required value={disableForm.password}
              onChange={e => setDisableForm({ ...disableForm, password: e.target.value })} style={{ width: 160 }} />
            <input placeholder="Code MFA" required value={disableForm.code}
              onChange={e => setDisableForm({ ...disableForm, code: e.target.value })} style={{ width: 120 }} />
            <button className="btn btn-danger btn-sm" disabled={busy}>Désactiver</button>
            <button type="button" className="btn btn-sm" onClick={() => setShowDisable(false)}>Annuler</button>
          </form>
        )}
      </div>

      {/* Exigence MFA entreprise (ADMIN) */}
      {isAdmin && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 18 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.92rem' }}>
            <input type="checkbox" checked={!!company?.mfaRequired} onChange={toggleOrgMfa} />
            Exiger la double authentification pour tous les membres de l'entreprise
          </label>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', margin: '6px 0 0 26px' }}>
            Chaque membre devra configurer la MFA à sa prochaine connexion.
          </p>
        </div>
      )}

      {/* Sessions */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: isAdmin ? 18 : 0 }}>
        <button className="btn btn-sm" onClick={logoutAll}>
          <LogOut size={13} /> Déconnecter toutes mes sessions
        </button>
      </div>

      {/* Journal d'audit (ADMIN) */}
      {isAdmin && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <button className="btn btn-sm" onClick={() => setShowLog(!showLog)}>
            <ScrollText size={13} /> {showLog ? 'Masquer' : 'Afficher'} le journal de sécurité
          </button>
          {showLog && (
            <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }}>
              {!log ? (
                <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>Chargement...</p>
              ) : log.length === 0 ? (
                <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>Aucun événement.</p>
              ) : (
                <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-light)' }}>
                      <th style={{ padding: '4px 6px' }}>Date</th>
                      <th style={{ padding: '4px 6px' }}>Événement</th>
                      <th style={{ padding: '4px 6px' }}>Compte</th>
                      <th style={{ padding: '4px 6px' }}>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map(e => (
                      <tr key={e.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                          {new Date(e.createdAt).toLocaleString('fr-FR')}
                        </td>
                        <td style={{ padding: '4px 6px' }}>{EVENT_LABELS[e.event] || e.event}</td>
                        <td style={{ padding: '4px 6px' }}>{e.email || '—'}</td>
                        <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{e.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
